import { ComponentProps, useEffect, useMemo, useRef } from 'react'
import { getGroupItemDisplayValue } from '~containers/app/ask-a-vet/utils'
import { cn } from '~utils/cn'
import { GroupItem } from '../../types'
import ExtraSelectBox from './extra-select-box'
import ExtraTextInput from './extra-text-input'

export type CheckBoxProps = Readonly<ComponentProps<'input'> & {}>
const CheckBoxSvg = ({ type = 'default', checked }: Pick<CheckBoxProps, 'type' | 'checked'>) => {
  return (
    <svg
      className={`h-4 w-4 stroke-white stroke-[3px] ${checked ? 'visible' : 'invisible'}`}
      style={{ strokeLinecap: 'round' }}
      viewBox="0 0 24 24"
    >
      {type === 'indetermined' ? (
        <line x1={5} y1={12} x2={19} y2={12} />
      ) : (
        <>
          <line x1={5} y1={11} x2={11} y2={17} />
          <line x1={11} y1={17} x2={18} y2={7} />
        </>
      )}
    </svg>
  )
}

interface CheckSelectProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  root: any
  item: GroupItem
  isSelected: boolean
  onSelect: (triggeredItem: GroupItem, isUnique: boolean) => void
}

const CheckSelect = ({ root, item, isSelected, onSelect }: CheckSelectProps) => {
  const initialized = useRef(false)
  const { extra_component } = item
  const displayValue = useMemo(() => getGroupItemDisplayValue(item), [item])

  const renderExtraComponent = useMemo(() => {
    if (!extra_component) {
      return null
    }
    const { type } = extra_component
    switch (type) {
      case 'select': {
        const { datafield, placeholder, options } = extra_component

        return (
          <ExtraSelectBox
            root={root}
            placeholder={placeholder}
            datafield={datafield}
            options={options}
            onSelect={event => onSelect(event, true)}
          />
        )
      }
      case 'text': {
        const { datafield, placeholder } = extra_component

        return (
          <ExtraTextInput
            root={root}
            placeholder={placeholder}
            datafield={datafield}
            onChange={event => {
              onSelect(event, true)
            }}
          />
        )
      }
      default:
        return null
    }
  }, [root, extra_component, onSelect])

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true
      if (!extra_component) {
        return
      }

      const { type } = extra_component

      if (root[extra_component.datafield] === undefined) {
        switch (type) {
          case 'select':
            {
              const { datafield } = extra_component
              onSelect({ datafield, value: null, extra_component }, true)
            }
            break
          case 'text':
            {
              const { datafield } = extra_component
              onSelect({ datafield, value: null, extra_component }, true)
            }
            break
        }
      }
    }
  }, [extra_component, onSelect, root])

  return (
    <button
      type="button"
      // 체크 표시가 그림(svg)이라 보조 기술이 선택 상태를 알 수 있게 체크박스 역할과 상태를 붙인다
      role="checkbox"
      aria-checked={isSelected}
      className={cn(
        'flex flex-col w-full rounded-[8px] px-4 py-3 transition-all duration-300',
        isSelected && 'bg-white dark:bg-gray-800',
        !isSelected && 'bg-transparent',
        isSelected && renderExtraComponent && 'flex-gap-y-3'
      )}
      onClick={() => {
        onSelect(item, false)
      }}
    >
      <div className="w-full flex-row flex items-center flex-gap-x-4">
        <div
          className={cn('inline-block rounded-sm border border-solid', {
            'border-primary-100 bg-primary-100 dark:bg-secondary-500 dark:border-secondary-500':
              isSelected,
            'border-primary-500 bg-primary-500 active:border-primary-600 active:bg-primary-600 group-hover:border-primary-400 group-hover:bg-primary-400':
              isSelected,
            'border-gray-400 bg-white dark:bg-background': !isSelected,
          })}
        >
          <CheckBoxSvg type="default" checked={isSelected} />
        </div>
        <span
          className={cn(
            'text-start text-body-1 text-gray-900 dark:text-gray-500',
            isSelected && 'dark:text-white'
          )}
        >
          {displayValue}
        </span>
      </div>
      {isSelected && renderExtraComponent}
    </button>
  )
}

export default CheckSelect
